import React, { Suspense, useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { App as AntdApp, ConfigProvider, Grid, Spin, theme } from 'antd';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { SystemBars, SystemBarsStyle } from '@capacitor/core';
import { NavigationBar } from '@hugotomazi/capacitor-navigation-bar';
import { COLORS } from './Constants/design';

// ... (existing imports)

import './App.css';
import Router from './Routes';
import { GlobalProvider } from './Components/Shared/GlobalContext/GlobalContext';
import LoadingOverlay from './Components/Shared/LoadingOverlay/LoadingOverlay';
import { useTheme } from './Components/Shared/GlobalContext/ThemeContext';
import { useTranslation } from 'react-i18next';
import enUS from 'antd/es/locale/en_US';
import frFR from 'antd/es/locale/fr_FR';
import esES from 'antd/es/locale/es_ES';
import deDE from 'antd/es/locale/de_DE';
import zhCN from 'antd/es/locale/zh_CN';
import arEG from 'antd/es/locale/ar_EG';
import i18n from './i18n';
import { RTL_LANGUAGES } from './Constants/language';

const antdLocales = {
  en: enUS,
  fr: frFR,
  es: esES,
  de: deDE,
  zh: zhCN,
  ar: arEG
};
const { useBreakpoint } = Grid;

function App() {
  const { t } = useTranslation();
  const [antdLocale, setAntdLocale] = useState(antdLocales.en);
  const [direction, setDirection] = useState('ltr');

  const { isDarkMode } = useTheme();
  const screens = useBreakpoint() || {};
  const isMobile = !!screens?.xs;

  // Global error handler - logs errors to console without breaking UI
  useEffect(() => {
    const handleError = (event) => {
      console.error('Global JavaScript Error:', event.error);
      console.error('Error details:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
      // Prevent the default browser error handling
      event.preventDefault();
    };

    const handleUnhandledRejection = (event) => {
      console.error('Unhandled Promise Rejection:', event.reason);
      // Prevent the default browser unhandled rejection handling
      event.preventDefault();
    };

    // Add global error event listeners
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Capacitor Hardware Back Button Handling
    let backButtonListener;
    if (Capacitor.isNativePlatform()) {
      // 1. Initial StatusBar setup - done once on mount to avoid conflicts during theme switches
      // StatusBar.show().catch(e => console.error('StatusBar show error:', e));
      // StatusBar.setOverlaysWebView({ overlay: false }).catch(e => console.error('StatusBar overlay error:', e));

      backButtonListener = CapApp.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          // No more history: exit the app
          CapApp.exitApp();
        } else {
          // A modal is likely open or we have page history.
          // Calling window.history.back() triggers the real 'popstate' event
          // which your existing useMobileFullscreenModalBackHandler hook is listening for.
          window.history.back();
        }
      });
    }

    // Cleanup function
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      if (backButtonListener) {
        backButtonListener.remove();
      }
    };
  }, []);

  const { darkAlgorithm, defaultAlgorithm } = theme;
  const themeConfig = {
    algorithm: isDarkMode
      ? darkAlgorithm
      : defaultAlgorithm,
    token: {
      colorPrimary: COLORS.primary,
      ...(isDarkMode ? {
        colorBgBase: COLORS.dark.background,
        colorBgLayout: COLORS.dark.background,
        colorBgContainer: COLORS.dark.elevated,
        colorBgElevated: COLORS.dark.elevated,
        colorBorder: COLORS.dark.controlBorder,
        colorBorderSecondary: COLORS.dark.border,
        colorText: COLORS.dark.text,
        colorTextSecondary: COLORS.dark.textSecondary,
      } : {
        colorBgBase: COLORS.light.background,
        colorBgLayout: COLORS.light.background,
        colorBgContainer: COLORS.light.surface,
        colorBgElevated: COLORS.light.elevated,
        colorBorder: COLORS.light.border,
        colorBorderSecondary: COLORS.light.border,
        colorText: COLORS.light.text,
        colorTextSecondary: COLORS.light.textSecondary,
      }),
    },
    components: {
      Card: {
        padding: screens.md ? 12 : 24,
      },
      Input: {
        activeBg: isDarkMode ? COLORS.dark.surface : COLORS.light.surface,
        hoverBg: isDarkMode ? COLORS.dark.surface : COLORS.light.surface,
        addonBg: isDarkMode ? COLORS.dark.elevated : COLORS.light.elevated,
        colorBorder: isDarkMode ? COLORS.dark.controlBorder : COLORS.light.border,
      },
      InputNumber: {
        activeBg: isDarkMode ? COLORS.dark.surface : COLORS.light.surface,
        hoverBg: isDarkMode ? COLORS.dark.surface : COLORS.light.surface,
        colorBorder: isDarkMode ? COLORS.dark.controlBorder : COLORS.light.border,
      },
      Select: {
        selectorBg: isDarkMode ? COLORS.dark.surface : COLORS.light.surface,
        colorBorder: isDarkMode ? COLORS.dark.controlBorder : COLORS.light.border,
      },
      Modal: {
        borderRadiusLG: isMobile ? 0 : 8,
      }
    },
  };

  useEffect(() => {
    const updateAntdLocale = () => {
      const lang = i18n.language.split('-')[0];
      setAntdLocale(antdLocales[lang] || antdLocales.en);

      setDirection(RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr');
    };
    updateAntdLocale();
    i18n.on('languageChanged', updateAntdLocale);
    return () => i18n.off('languageChanged', updateAntdLocale);
  }, []);

  // Update the root element's data-theme attribute and StatusBar when dark mode changes
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');

    if (Capacitor.getPlatform() === 'android') {
      const updateStatusBar = async () => {
        const bgColor = isDarkMode ? COLORS.dark.header : COLORS.light.header;
        const style = isDarkMode ? SystemBarsStyle.Dark : SystemBarsStyle.Light;

        try {
          await EdgeToEdge.setStatusBarColor({ color: bgColor });
          await EdgeToEdge.setNavigationBarColor({ color: bgColor });
          await SystemBars.setStyle({ style });
          await NavigationBar.setColor({ darkButtons: !isDarkMode });
        } catch (e) {
          console.error('[StatusBar] Error:', e);
        }
      };

      updateStatusBar();
    }
  }, [isDarkMode]);

  return (
    <ConfigProvider
      theme={themeConfig}
      locale={antdLocale}
      direction={direction}
    >
      <AntdApp>
        <Suspense
          fallback={(
            <div className="route-loading" role="status" aria-live="polite">
              <Spin size="large" tip={t('loading...')} />
            </div>
          )}
        >
          <GlobalProvider>
            <LoadingOverlay />
            <RouterProvider router={Router} future={{ v7_startTransition: true }} />
          </GlobalProvider>
        </Suspense>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
